import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, RotateCcw } from 'lucide-react'
import type { WorkflowConfig } from '@kfolks/types'
import { Button } from '@/shared/components/atoms/ui/button'
import { canonicalEquals } from '@/modules/shared/json-canonicalize'
import { useWorkflowBuilderState } from './WorkflowBuilderState'

/**
 * Raw JSON editor for the workflow builder, wired to the shared
 * `WorkflowBuilderState` so edits round-trip with the Visual tab.
 *
 * Mirrors `form-builder/RawJsonEditor.tsx` — same live-commit-on-valid,
 * same draft-state-with-banner-on-invalid, same `lastReflectedRef`
 * pattern for external-change reflow, same Reset-to-current. Per design
 * D12 the Raw JSON tab is always reachable; this is the **state-bridged**
 * editor that lives under it (the standalone Spec 2 `WorkflowEditorPage`
 * has been retired — it had its own load/save cycle disconnected from
 * the builder context, which the new tab fixes).
 *
 * **Live commit on valid JSON**: every keystroke is parsed; if it
 * produces a structurally-valid `WorkflowConfig` (object with
 * `version: '1.0'` and a `steps` array), it's committed to the shared
 * state immediately. Switching to Visual then sees the JSON edits.
 *
 * **No autosave to the server** — save is a builder-wide action
 * (task 8.9) accessible from any tab. "Reset to current" is the
 * abandon-this-draft path.
 *
 * Per ADR-0012, the canonical shape is `{ version: '1.0', steps: [...] }`
 * — the parser deliberately rejects XState-style payloads with
 * `initial` / `states` keys at the top level.
 */
export function RawJsonEditor() {
	const { workflowConfig, setWorkflowConfig } = useWorkflowBuilderState()
	const [draft, setDraft] = useState<string>(() => stringify(workflowConfig))

	// Track which shared-state config the textarea was last reflected from.
	// Reflow rule: if the admin hasn't typed since the last reflow (i.e.,
	// `draft` still canonicalises to that snapshot), reflow on every external
	// change. Once they type, leave the draft alone until they Reset or
	// commit valid JSON.
	const lastReflectedRef = useRef<WorkflowConfig>(workflowConfig)

	useEffect(() => {
		const parsed = safeParse(draft)
		if (parsed.ok && canonicalEquals(parsed.value, lastReflectedRef.current)) {
			setDraft(stringify(workflowConfig))
			lastReflectedRef.current = workflowConfig
		}
	}, [workflowConfig])

	const parseStatus = useMemo(() => safeParse(draft), [draft])

	useEffect(() => {
		if (parseStatus.ok && !canonicalEquals(parseStatus.value, workflowConfig)) {
			setWorkflowConfig(parseStatus.value)
			lastReflectedRef.current = parseStatus.value
		}
	}, [parseStatus])

	const reset = () => {
		setDraft(stringify(workflowConfig))
		lastReflectedRef.current = workflowConfig
	}

	return (
		<div className="space-y-3" aria-label="Raw JSON editor">
			<div className="flex items-center justify-between gap-2">
				<p className="text-sm text-muted-foreground">
					Edits here flow to the Visual tab. Save from anywhere persists the result.
				</p>
				<Button variant="outline" size="sm" onClick={reset} aria-label="Reset to current workflow">
					<RotateCcw className="size-4" /> Reset to current
				</Button>
			</div>

			<textarea
				aria-label="Workflow config JSON"
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				rows={24}
				spellCheck={false}
				className={
					'w-full rounded-md border bg-background p-3 font-mono text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
					(parseStatus.ok ? 'border-input' : 'border-destructive')
				}
			/>

			{!parseStatus.ok ? (
				<div
					role="alert"
					className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive"
				>
					<AlertCircle className="mt-0.5 size-4 shrink-0" />
					<div>
						<p className="font-medium">JSON parse error</p>
						<p className="mt-0.5 text-xs">{parseStatus.error}</p>
						<p className="mt-1 text-xs">
							Edits remain in this textarea (draft) — the Visual tab will not see them until the JSON is valid again.
						</p>
					</div>
				</div>
			) : null}
		</div>
	)
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function stringify(config: WorkflowConfig): string {
	return JSON.stringify(config, null, 2)
}

type ParseStatus = { ok: true; value: WorkflowConfig } | { ok: false; error: string }

/**
 * Parse + minimal shape-check. We deliberately do NOT run
 * `validateWorkflowConfig` here (that's the debounced live-validation
 * path). Goal: accept anything structurally `WorkflowConfig`-shaped so
 * the Visual tab can render it; deeper invariants (unreachable step,
 * missing approver dimension, etc.) surface as inline validation
 * errors elsewhere, not as JSON parse errors.
 *
 * Per ADR-0012, the canonical shape is `{ version: '1.0', steps: [...] }`.
 * We explicitly reject XState-style payloads with `initial` / `states`
 * keys to make the contract decision visible at the parse layer.
 */
function safeParse(text: string): ParseStatus {
	let parsed: unknown
	try {
		parsed = JSON.parse(text)
	} catch (err) {
		return { ok: false, error: err instanceof Error ? err.message : 'Invalid JSON' }
	}
	if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
		return { ok: false, error: 'Top-level value must be an object' }
	}
	const obj = parsed as Record<string, unknown>
	if ('initial' in obj || 'states' in obj) {
		return {
			ok: false,
			error:
				'XState-style payloads with `initial` / `states` are not accepted (per ADR-0012, the workflow contract is a linear step-array — `{ version: "1.0", steps: [...] }`).',
		}
	}
	if (obj.version !== '1.0') {
		return { ok: false, error: `version must be "1.0" (got ${JSON.stringify(obj.version)})` }
	}
	if (!Array.isArray(obj.steps)) {
		return { ok: false, error: '`steps` must be an array' }
	}
	return { ok: true, value: obj as unknown as WorkflowConfig }
}
